import 'rc-slider/assets/index.css';

import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import Slider from 'rc-slider';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IndicatorDriver } from 'models/response/driver.interface';
import { InputAdjutment } from 'models/requestBody/getModelOutputs.interface';
import { GetInputAdjustments } from 'redux/driverAdjustmentSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampTypography';
import { accent, text as textToken } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { usePlanPageRevampVariant } from 'pages/PlanRevamp/PlanPageUIRevamp/usePlanPageUiRevamp';

import '../PlanPageUIRevamp/planWhatIfsSlider.css';

interface Properties {
  driver: IndicatorDriver;
  onAdjustInput: (parameter: InputAdjutment) => void;
  isResetClicked: boolean;
  setIsResetClicked: (value: boolean) => void;
  variant?: 'legacy' | 'revamp';
  embeddedInWhatIfsPanel?: boolean;
}

const SliderComponent = ({
  driver,
  onAdjustInput,
  isResetClicked,
  setIsResetClicked,
  variant = 'legacy',
  embeddedInWhatIfsPanel = false,
}: Properties) => {
  const isRevamp = usePlanPageRevampVariant(variant);
  const isWhatIfsPanel = isRevamp && embeddedInWhatIfsPanel;
  const themeColors = useSelector(getPlanPageThemeColors);
  const Value = useSelector(GetInputAdjustments);
  const sliderValue = Value !== null && Value[driver.id] ? Value[driver.id] : 0;
  const [currentSliderValue, setCurrentSliderValue] = useState(sliderValue);
  const baseScenario = useSelector(getBaseScenario);

  useEffect(() => {
    setCurrentSliderValue(sliderValue);
  }, [sliderValue]);

  useEffect(() => {
    if (isResetClicked) {
      setCurrentSliderValue(0);
      setIsResetClicked(false);
    }
  }, [isResetClicked, setIsResetClicked]);

  useEffect(() => {
    setCurrentSliderValue(0);
  }, [baseScenario?.id]);

  const setSliderValue = (value: number | number[]) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    onAdjustInput({ [driver.id]: nextValue });
  };

  const trackColor = isWhatIfsPanel ? undefined : isRevamp ? themeColors.primary : 'white';
  const railColor = isWhatIfsPanel ? undefined : isRevamp ? themeColors.border : '#CDCDCD66';

  return (
    <VStack
      w='full'
      spacing={isWhatIfsPanel ? 1 : isRevamp ? 2 : '1'}
      width={isRevamp ? '100%' : '90%'}
      align='stretch'
    >
      <HStack w='full' justify='space-between' align='center'>
        <Text
          {...(isWhatIfsPanel ? PLAN_TEXT.labelSm : undefined)}
          fontSize={isRevamp ? '12px' : undefined}
          fontWeight={isWhatIfsPanel ? undefined : isRevamp ? '500' : undefined}
          color={isWhatIfsPanel ? textToken.secondary : isRevamp ? themeColors.textDark : undefined}
          size={isRevamp ? undefined : 'modalContent'}
          noOfLines={1}
          flex='1'
          pr={isWhatIfsPanel ? 0 : 2}
        >
          {driver.name}
        </Text>
        <Text
          {...(isWhatIfsPanel ? PLAN_TEXT.labelSmMedium : undefined)}
          fontSize={isRevamp ? '12px' : undefined}
          fontWeight={isWhatIfsPanel ? undefined : isRevamp ? '600' : undefined}
          size={isRevamp ? undefined : 'modalContent'}
          py={isWhatIfsPanel ? '2px' : isRevamp ? 1 : '2'}
          px={isWhatIfsPanel ? '8px' : isRevamp ? 2 : '1'}
          minW={isWhatIfsPanel ? '40px' : isRevamp ? '44px' : '11'}
          textAlign='center'
          borderRadius={isWhatIfsPanel ? 'full' : isRevamp ? '8px' : 'xs'}
          bg={isWhatIfsPanel ? accent.subtle : isRevamp ? themeColors.activeBg : undefined}
          bgColor={isWhatIfsPanel ? undefined : isRevamp ? undefined : 'violet.50'}
          color={isWhatIfsPanel ? accent.solid : isRevamp ? themeColors.primary : undefined}
        >
          {currentSliderValue}%
        </Text>
      </HStack>
      <Box h={isWhatIfsPanel ? '16px' : undefined}>
        <Slider
          className={isWhatIfsPanel ? 'plan-whatifs-slider-revamp' : undefined}
          min={-100}
          max={100}
          startPoint={0}
          value={currentSliderValue}
          onChange={setCurrentSliderValue}
          onAfterChange={setSliderValue}
          trackStyle={
            isWhatIfsPanel ? undefined : { backgroundColor: trackColor, height: isRevamp ? 6 : 8 }
          }
          railStyle={
            isWhatIfsPanel ? undefined : { backgroundColor: railColor, height: isRevamp ? 6 : 8 }
          }
          handleStyle={
            isWhatIfsPanel
              ? undefined
              : {
                  borderColor: isRevamp ? themeColors.primary : '#CDCDCD66',
                  backgroundColor: isRevamp ? themeColors.surface : undefined,
                  height: isRevamp ? 14 : 16,
                  width: isRevamp ? 14 : 16,
                  marginTop: isRevamp ? -4 : undefined,
                }
          }
        />
      </Box>
    </VStack>
  );
};

export default SliderComponent;
